
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { importCompaniesSkipExisting } from '@/utils/companyImport';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Info, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';

const CompanyImport: React.FC = () => {
  const [companyList, setCompanyList] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ added: number; skipped: number } | null>(null);

  const handleImport = () => {
    if (!companyList.trim()) {
      toast.error('Please enter a list of companies');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const importResult = importCompaniesSkipExisting(companyList, delimiter);
      setResult(importResult);
      
      toast.success(`Successfully imported ${importResult.added} companies`, {
        description: importResult.skipped > 0 
          ? `${importResult.skipped} companies were skipped as they already exist.` 
          : undefined
      });
    } catch (error) {
      console.error('Error importing companies:', error);
      toast.error('Error importing companies');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-redflags-red" />
          Import Companies
        </CardTitle>
        <CardDescription>
          Bulk import companies from a list
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="bg-redflags-light/20 border-redflags-red/30">
          <Info className="h-4 w-4 text-redflags-red" />
          <AlertTitle>Format Instructions</AlertTitle>
          <AlertDescription>
            Enter each company on a new line with the format:<br />
            <code>Company Name{delimiter} Location{delimiter} Staff Size</code><br />
            Only Company Name is required.
          </AlertDescription>
        </Alert>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Delimiter</label>
          <Select value={delimiter} onValueChange={setDelimiter}>
            <SelectTrigger className="w-full max-w-[200px]">
              <SelectValue placeholder="Select a delimiter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=",">Comma (,)</SelectItem>
              <SelectItem value=";">Semicolon (;)</SelectItem>
              <SelectItem value="|">Pipe (|)</SelectItem>
              <SelectItem value="\t">Tab</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Company List</label>
          <Textarea 
            value={companyList}
            onChange={(e) => setCompanyList(e.target.value)}
            placeholder="Company 1, Sydney, 500
Company 2, Melbourne, 1200
Company 3, Brisbane, 300"
            className="min-h-[200px] font-mono"
          />
        </div>
        
        {result && (
          <Alert className="bg-green-50 border-green-200">
            <AlertCircle className="h-4 w-4 text-green-600" />
            <AlertTitle>Import Results</AlertTitle>
            <AlertDescription>
              Added: {result.added} companies<br />
              Skipped: {result.skipped} companies (already exist)
            </AlertDescription>
          </Alert>
        )}
        
        <Button 
          onClick={handleImport} 
          disabled={isLoading || !companyList.trim()}
          className="bg-redflags-red hover:bg-redflags-red/90 text-white"
        >
          {isLoading ? 'Importing...' : 'Import Companies'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CompanyImport;
